import type { MapStyle } from 'shared/mapStyles/domain/mapStyle';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

export const localStorageMapStyleKey = 'mapStyle';

export const useMapStyleStore = defineStore('mapStyle', () => {
  const initialized = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const styles = ref<MapStyle[]>([]);
  const selectedStyleId = ref<string>();

  const selectedIdText = localStorage.getItem(localStorageMapStyleKey);

  if (selectedIdText) {
    selectedStyleId.value = selectedIdText;
  }

  const selectedStyle = computed(() => {
    // Try to get the currently selected style
    if (selectedStyleId.value !== undefined) {
      const style = getById(selectedStyleId.value);
      if (style) return style;
    }
    
    // Fall back to first style if current ID is not found or not set
    if (styles.value.length > 0) {
      const firstStyle = styles.value[0]!;
      // Update the selected ID to the first style
      if (selectedStyleId.value !== firstStyle.id) {
        selectedStyleId.value = firstStyle.id;
      }
      return firstStyle;
    }
    
    return undefined;
  });

  watch(selectedStyleId, (newStyleId) => {
    if (newStyleId === undefined) {
      localStorage.removeItem(localStorageMapStyleKey);
    } else {
      localStorage.setItem(localStorageMapStyleKey, newStyleId.toString());
    }
  });

  function getById(id: string) {
    for (const style of styles.value) {
      if (style.id === id) return style;
    }
  }

  function merge(style: MapStyle) {
    for (let i = 0; i < styles.value.length; i++) {
      const existing = styles.value[i];
      if (existing && existing.id === style.id) {
        styles.value[i] = style;
        return;
      }
    }

    styles.value.push(style);
  }

  function mergeList(list: MapStyle[]) {
    styles.value = list;
    if (selectedStyleId.value === undefined && list.length > 0) {
      selectedStyleId.value = list[0]?.id;
    }
  }

  function deleteStyle(id: string) {
    for (let i = 0; i < styles.value.length; i++) {
      const existing = styles.value[i];
      if (existing && existing.id === id) {
        styles.value.splice(i, 1);
        return;
      }
    }
  }

  return {
    merge,
    mergeList,
    deleteStyle,
    initialized,
    loading,
    selectedStyle,
    styles,
    selectedStyleId,
  };
});
